const BASE = '/ai/procurement/gm-assistant';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ENGINE = 'native_procurement_v1';
export const PROCUREMENT_QUESTIONS = Object.freeze([
  'Which purchase orders are overdue?', 'Which deliveries are expected next week?',
  'Which suppliers have the most rejection records?', 'Show backorders',
  'Show invoice variances that need review',
]);
function invalid(code = 'AI_CLIENT_RESPONSE_INVALID') { throw Object.assign(new Error('The response could not be confirmed. Review saved conversations before trying again.'), { uncertain: true, code }); }
function uuid(value) { if (typeof value !== 'string' || !UUID.test(value)) invalid(); return value.toLowerCase(); }
function text(value, max) {
  if (typeof value !== 'string' || !value.trim() || value.includes('\0') || Array.from(value).length > max) invalid();
  return value;
}
function date(value, optional = false) {
  if (optional && value == null) return;
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) invalid();
}
function cursor(value) {
  if (value === null) return null;
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{1,512}$/.test(value)) invalid();
  return value;
}
function header(value) {
  uuid(value?.conversation_uuid); if (value?.title != null) text(value.title, 160);
  if (!['active', 'archived'].includes(value?.status)) invalid();
  date(value.created_at); date(value.updated_at); date(value.last_message_at, true);
  return value;
}
export function procurementContextKey(user) {
  const ids = [user?.id, user?.company_id, user?.unit_id];
  if (ids.some((id) => !/^[1-9][0-9]*$/.test(String(id ?? '')) || !Number.isSafeInteger(Number(id)))) return null;
  return JSON.stringify([...ids.map(String), user.role, [...(user.roles || [])].sort(),
    [...(user.permissions || [])].sort(), [...(user.allowedCostCentres || [])].map(String).sort(),
    user.cost_centre_scope, user.all_cost_centres, user.has_all_cost_centres, user.is_platform_admin]);
}
export function assistantError(error) {
  const status = error?.response?.status, code = error?.response?.data?.code;
  if (error?.code === 'AI_CLIENT_QUESTION_INVALID') return 'Enter a valid question within 2,000 characters.';
  if (error?.code === 'AI_CLIENT_CONTEXT_CHANGED') return 'The active account or unit changed. Refresh the page to reload your session.';
  if (status === 401) return 'Your session is unavailable. Sign in again.';
  if (status === 403) return 'You do not have access to this information in the selected unit.';
  if (status === 404) return 'This conversation or answer is unavailable in your current access.';
  if (status === 409) return 'This conversation changed or cannot be continued. Reload it or start a new conversation.';
  if (code === 'AI_ASSISTANT_RANGE_UNSUPPORTED') return 'Put the date period in your question.';
  if (status === 400 || status === 422) return 'Check the question, selection or feedback details.';
  return 'The result could not be confirmed. Review saved conversations before trying again.';
}
export function assistantOutcomeUncertain(error) {
  if (error?.code === 'AI_CLIENT_QUESTION_INVALID') return false;
  const status = error?.response?.status;
  return Boolean(error?.uncertain || !status || status === 408 || status >= 500);
}

export function createProcurementAssistantApi(client, expectedContext) {
  const expected = ['company_id', 'unit_id', 'user_id'].map((key) => String(expectedContext?.[key] ?? ''));
  if (expected.some((id) => !/^[1-9][0-9]*$/.test(id) || !Number.isSafeInteger(Number(id)))) invalid();
  function envelope(value) {
    if (value?.version !== 1 || value.engine !== ENGINE || value.model_used !== false) invalid();
    if (['company_id', 'unit_id', 'user_id'].some((key, index) => String(value.context?.[key]) !== expected[index])) invalid('AI_CLIENT_CONTEXT_CHANGED');
    uuid(value.request_id);
    return value;
  }
  const read = async (path, signal, params = {}) => envelope((await client.get(BASE + path, { signal, params })).data);
  return {
    async capabilities(signal) {
      const value = await read('/capabilities', signal);
      if (value.ready !== true || !['history', 'feedback', 'followups', 'source_labels'].every((key) => value[key] === true)) invalid();
      return value;
    },
    async ask(question, { conversationUuid, signal } = {}) {
      if (typeof question !== 'string' || !question.trim() || Array.from(question).length > 2000 ||
          /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(question)) {
        throw Object.assign(new Error('Enter a question within 2,000 characters.'), { code: 'AI_CLIENT_QUESTION_INVALID' });
      }
      const body = { question: question.trim(), ...(conversationUuid ? { conversation_uuid: uuid(conversationUuid) } : {}) };
      const value = envelope((await client.post(BASE, body, { signal })).data);
      if (!['answer', 'clarify', 'action_request'].includes(value.kind) ||
          value.source_type !== (value.kind === 'answer' ? 'live_system' : 'none') || value.advisory_only !== true ||
          value.question !== body.question || !Array.isArray(value.sections) ||
          (value.kind === 'answer' ? value.sections.length < 1 || value.sections.length > 3 : value.sections.length !== 0)) invalid();
      uuid(value.conversation_uuid); uuid(value.user_message_uuid); uuid(value.assistant_message_uuid);
      if (conversationUuid && value.conversation_uuid !== body.conversation_uuid) invalid();
      text(value.answer, 20000); date(value.generated_at);
      if (!Array.isArray(value.suggestions) || value.suggestions.length > 5) invalid();
      value.suggestions.forEach((item) => text(item, 2000));
      return value;
    },
    async list({ before = null, signal } = {}) {
      const value = await read('/conversations', signal, { limit: 20, ...(before ? { cursor: cursor(before) } : {}) });
      if (!Array.isArray(value.items) || value.items.length > 20) invalid();
      const ids = new Set();
      value.items.forEach((item) => { header(item); if (ids.has(item.conversation_uuid)) invalid(); ids.add(item.conversation_uuid); });
      cursor(value.next_cursor);
      if (before && value.next_cursor === before) invalid();
      return value;
    },
    async conversation(conversationUuid, { before = null, signal } = {}) {
      const target = uuid(conversationUuid);
      const value = await read('/conversations/' + target, signal, { limit: 40, ...(before ? { cursor: cursor(before) } : {}) });
      header(value.conversation);
      if (value.conversation.conversation_uuid !== target || !Array.isArray(value.messages) || value.messages.length > 40) invalid();
      const ids = new Set();
      value.messages.forEach((message) => {
        uuid(message.message_uuid); text(message.content, 20000); date(message.created_at);
        if (ids.has(message.message_uuid)) invalid(); ids.add(message.message_uuid);
        if (!['user', 'assistant'].includes(message.role) || message.advisory_only !== (message.role === 'assistant') ||
            (message.role === 'user' ? message.source_type !== 'none' : !['none', 'live_system'].includes(message.source_type))) invalid();
      });
      cursor(value.next_cursor);
      if (before && value.next_cursor === before) invalid();
      return value;
    },
    async feedback(messageUuid, rating, signal) {
      if (rating !== 1 && rating !== -1) throw new Error('Choose Helpful or Not helpful.');
      const target = uuid(messageUuid);
      const value = envelope((await client.post(BASE + '/feedback', { message_uuid: target, rating }, { signal })).data);
      if (value.message_uuid !== target || value.rating !== rating) invalid();
      return value;
    },
  };
}
